import React, { useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Container, Card, Box, TextField, Button } from '@material-ui/core';
import WrapperSeamless from '../../layout-components/WrapperSeamless';
import Upload from '../../components/upload';
import * as Api from '../../service';
import useStore from '../../context';

const MAX_META_TITLE = 60;
const MAX_META_DESC = 160;
const SITE_URL = 'https://doodooc.com';
const PATH_REGEX = /^\/[a-z0-9\-/_'.]*$/;

const initForm = {
  pagePath: '',
  metaTitle: '',
  metaDescription: '',
  canonicalUrl: '',
  ogTitle: '',
  ogDescription: '',
  ogImage: ''
};

const SeoForm = () => {
  const { id } = useParams();
  const history = useHistory();
  const { setSuccessMsg, setErrorMsg } = useStore();
  const [form, setForm] = useState(initForm);
  const [ogFile, setOgFile] = useState(null);

  useEffect(() => {
    if (!id) return;
    Api.seo.getById(id)
      .then((data) =>
        setForm({
          pagePath: data.pagePath || '',
          metaTitle: data.metaTitle || '',
          metaDescription: data.metaDescription || '',
          canonicalUrl: data.canonicalUrl || '',
          ogTitle: data.ogTitle || '',
          ogDescription: data.ogDescription || '',
          ogImage: data.ogImage || ''
        })
      )
      .catch((e) => setErrorMsg(e.message));
  }, [id, setErrorMsg]);

  const updateField = (e) => {
    const { name, value } = e.target;
    setForm((st) => ({ ...st, [name]: value }));
  };

  const uploadOg = async () => {
    if (!ogFile?.length) return;
    try {
      const data = new FormData();
      data.append('context', 'BLOG_OG');
      data.append('file', ogFile[0]);
      const res = await Api.blog.uploadImage(data);
      setForm((st) => ({ ...st, ogImage: res.url || '' }));
      setSuccessMsg('OG image uploaded');
    } catch (e) {
      setErrorMsg(e.message);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.pagePath) return setErrorMsg('Page path is required');
    if (!PATH_REGEX.test(form.pagePath.toLowerCase()))
      return setErrorMsg("Path must start with / and contain only lowercase letters, numbers, dashes, slashes");
    try {
      const payload = JSON.stringify(form);
      if (id) {
        await Api.seo.update(id, payload);
      } else {
        await Api.seo.create(payload);
      }
      setSuccessMsg('saved');
      history.push('/seo');
    } catch (err) {
      setErrorMsg(err.message);
    }
  };

  const canonicalPreview =
    form.canonicalUrl || (form.pagePath ? `${SITE_URL}${form.pagePath}` : '');

  return (
    <Container>
      <WrapperSeamless sectionHeading={id ? 'Edit Page Meta' : 'Add Page Meta'} backUrl="/seo">
        <Box component="form" onSubmit={save}>
          <Card className="shadow-xxl px-4 py-3 my-4">
            <div className="heading-3 mb-2">Page</div>
            <TextField
              className="my-2"
              label="Page Path (e.g. /pricing)"
              variant="outlined"
              name="pagePath"
              fullWidth
              value={form.pagePath}
              onChange={updateField}
            />
          </Card>

          <Card className="shadow-xxl px-4 py-3 my-4">
            <div className="heading-3 mb-2">SEO</div>
            <TextField
              className="my-2"
              label={`Meta Title (${form.metaTitle.length}/${MAX_META_TITLE})`}
              variant="outlined"
              name="metaTitle"
              fullWidth
              value={form.metaTitle}
              onChange={updateField}
              helperText={
                form.metaTitle.length > MAX_META_TITLE
                  ? `Over the recommended ${MAX_META_TITLE} characters — Google may truncate it`
                  : ''
              }
            />
            <TextField
              className="my-2"
              label={`Meta Description (${form.metaDescription.length}/${MAX_META_DESC})`}
              variant="outlined"
              name="metaDescription"
              fullWidth
              multiline
              value={form.metaDescription}
              onChange={updateField}
              helperText={
                form.metaDescription.length > MAX_META_DESC
                  ? `Over the recommended ${MAX_META_DESC} characters — Google may truncate it`
                  : ''
              }
            />
            <Card className="p-3 my-2">
              <div className="text-black-50 font-size-sm">Google Preview</div>
              <div className="font-size-sm text-primary">
                {canonicalPreview || `${SITE_URL}/your-page`}
              </div>
              <div className="font-size-lg font-weight-bold">
                {form.metaTitle || 'Meta Title'}
              </div>
              <div className="font-size-sm text-black-50">
                {form.metaDescription || 'Meta description preview...'}
              </div>
            </Card>
            <TextField
              className="my-2"
              label="Canonical URL"
              variant="outlined"
              name="canonicalUrl"
              fullWidth
              value={form.canonicalUrl}
              onChange={updateField}
              helperText={form.canonicalUrl ? 'Custom canonical URL' : "If blank, uses the page's own URL"}
            />
          </Card>

          <Card className="shadow-xxl px-4 py-3 my-4">
            <div className="heading-3 mb-2">Open Graph</div>
            <TextField
              className="my-2"
              label="OG Title"
              variant="outlined"
              name="ogTitle"
              fullWidth
              value={form.ogTitle}
              onChange={updateField}
            />
            <TextField
              className="my-2"
              label="OG Description"
              variant="outlined"
              name="ogDescription"
              fullWidth
              multiline
              value={form.ogDescription}
              onChange={updateField}
            />
            <TextField
              className="my-2"
              label="OG Image URL"
              variant="outlined"
              name="ogImage"
              fullWidth
              value={form.ogImage}
              onChange={updateField}
            />
            {form.ogImage && (
              <img src={form.ogImage} alt="og" style={{ maxWidth: 240, marginTop: 12 }} />
            )}
            <Card className="p-3 my-3">
              <div className="heading-3 mb-2">Upload OG Image</div>
              <Upload single setNewFile={setOgFile} />
              <Button
                type="button"
                onClick={uploadOg}
                className="btn-primary mt-2"
                disabled={!ogFile?.length}>
                Upload OG
              </Button>
            </Card>
          </Card>

          <Button type="submit" className="btn-primary my-3">
            Save
          </Button>
        </Box>
      </WrapperSeamless>
    </Container>
  );
};

export default SeoForm;
